import { MenuItem, Text } from "@chakra-ui/react";
import React from "react";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { HasuraRole } from "src/types/hasuraRole";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkForm } from "./useEnqueueBulkForm";

const CONFIRMATION_BODY_TEXT =
  "This will execute form rules for all selected forms. Form rules will automatically tag forms based on configured conditions.";

export const BulkExecuteFormRules = () => {
  const toast = useAvelaToast();
  const bulk = useBulkActionTargets();
  const enqueueBulkForm = useEnqueueBulkForm();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, execute",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = React.useCallback(async () => {
    const count = bulk.selectionSize;
    if (count === 0) {
      toast.error({
        title: "No forms selected",
        description: "Please select at least one form.",
      });
      return;
    }

    trackFormAction({
      verb: "Clicked",
      actionType: "EXECUTE_FORM_RULES",
      bulk: true,
      count,
      async: true,
    });

    setHeader(
      <Text>{`Execute form rules for ${count} form${
        count !== 1 ? "s" : ""
      }?`}</Text>
    );
    setBody(<Text>{CONFIRMATION_BODY_TEXT}</Text>);

    if (await confirm()) {
      trackFormAction({
        verb: "Confirmed",
        actionType: "EXECUTE_FORM_RULES",
        bulk: true,
        count,
        async: true,
      });
      await enqueueBulkForm({
        actionType: "EXECUTE_FORM_RULES",
        actionData: {},
      });
    } else {
      trackFormAction({
        verb: "Canceled",
        actionType: "EXECUTE_FORM_RULES",
        bulk: true,
        count,
        async: true,
      });
    }
  }, [bulk, confirm, enqueueBulkForm, setBody, setHeader, toast]);

  return (
    <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={bulk.selectionSize === 0}
      >
        Execute form rules
      </MenuItem>
      {confirmationDialog}
    </WithRequiredHasuraRoles>
  );
};
